import { newestFirst } from '../utils/dateSort';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiExternalLink } from 'react-icons/fi';
import AnalyticsDateControl from '../components/AnalyticsDateControl';
import {
  AnalyticsAdvancedFilters,
  AnalyticsFilterActions,
  AnalyticsFilterBar,
  AnalyticsFilterGroup,
  AnalyticsPeriodGroup,
  AnalyticsPeriodPresets,
  AnalyticsPrimaryButton,
  AnalyticsRefreshButton,
  AnalyticsSelectField,
  AnalyticsTextField,
} from '../components/AnalyticsFilterBar';
import AnalyticsHeader from '../components/AnalyticsHeader';
import { MetricLabel } from '../components/MetricHelp';
import { useAnalyticsResource } from '../behavior/useAnalyticsResource';
import { formatMoment, formatRatio, hktToday, periodPreset, recentDays, validateRange } from '../utils/periodDashboard';
import type { DateRange } from '../utils/periodDashboard';
import '../components/AnalyticsWorkspace.css';
import './GenerationQualityPage.css';

interface OutcomeSummary {
  total: number;
  completed: number;
  failed: number;
  pending: number;
  unknown: number;
  terminal: number;
  successRate: number | null;
}

interface OutcomeBreakdown extends OutcomeSummary {
  mode?: string | null;
  date?: string;
}

export interface GenerationQualityData {
  period: DateRange & { days: number; timeZone: string };
  generatedAt: string;
  filters: { mode: string | null; modelId: string | null; provider: string | null };
  summary: OutcomeSummary;
  latency: {
    sampleCount: number;
    p50Ms: number | null;
    p95Ms: number | null;
    eligibleCount: number;
    missingCount: number;
    coverageRate: number | null;
    definition: string;
  };
  coverage: { population: string; status: string; notes: string[] };
  daily?: OutcomeBreakdown[];
  byMode?: OutcomeBreakdown[];
}

const MODE_OPTIONS = [
  { value: '', label: 'All creation modes' },
  { value: 'rebuild', label: 'Rebuild' },
  { value: 'blend', label: 'Blend' },
  { value: 'custom', label: 'Custom' },
  { value: 'extension', label: 'Lens' },
  { value: 'upscale', label: 'Upscale' },
];

function count(value: number | null | undefined): string {
  return typeof value === 'number' ? value.toLocaleString('en-US') : 'Unavailable';
}

function duration(value: number | null): string {
  if (value === null) return 'Unavailable';
  return value < 1000 ? `${Math.round(value)}ms` : `${(value / 1000).toFixed(1)}s`;
}

function Metric({ label, help, value, note }: { label: string; help: string; value: string; note: string }) {
  return <article className="analytics-metric"><span><MetricLabel label={label} help={help} /></span><strong>{value}</strong><small>{note}</small></article>;
}

function OutcomeTable({ rows, dimension, onMode }: {
  rows: OutcomeBreakdown[];
  dimension: 'mode' | 'date';
  onMode?: (mode: string) => void;
}) {
  const daily = dimension === 'date';
  const displayedRows = daily ? newestFirst(rows, row => row.date) : rows;
  return <div className="analytics-table-scroll" tabIndex={0} role="region" aria-label={daily ? 'Daily generation outcomes' : 'Generation performance by mode'}>
    <table className="analytics-table">
      <thead><tr>
        <th scope="col" aria-sort={daily ? 'descending' : undefined}>{daily ? 'HKT date ↓' : 'Creation mode'}</th>
        <th scope="col">Total persisted</th>
        <th scope="col">Server completed</th>
        <th scope="col">Failed</th>
        <th scope="col">Pending</th>
        <th scope="col">Unknown</th>
        <th scope="col"><MetricLabel label="Terminal success rate" help="Server completed / (server completed + failed). Pending and unknown outcomes are excluded. The terminal sample is shown below each rate." /></th>
      </tr></thead>
      <tbody>{displayedRows.map((row, index) => <tr key={(daily ? row.date : row.mode) || `unclassified-${index}`}>
        <td>{daily ? row.date || 'Date unavailable' : row.mode ? <button className="generation-quality-filter-link" type="button" onClick={() => onMode?.(row.mode!)}>{MODE_OPTIONS.find(option => option.value === row.mode)?.label || row.mode}</button> : 'Unclassified'}</td>
        <td>{count(row.total)}</td>
        <td>{count(row.completed)}</td>
        <td>{count(row.failed)}</td>
        <td>{count(row.pending)}</td>
        <td>{count(row.unknown)}</td>
        <td>{row.successRate === null ? 'No terminal outcomes' : formatRatio(row.successRate)}<small className="generation-quality-sample">{count(row.completed)} / {count(row.terminal)} terminal</small></td>
      </tr>)}</tbody>
    </table>
  </div>;
}

export default function GenerationQualityPage() {
  const [params, setParams] = useSearchParams();
  const requested = { startDate: params.get('startDate') || '', endDate: params.get('endDate') || '' };
  const range = validateRange(requested).valid ? requested : recentDays(30);
  const mode = params.get('mode') || '';
  const provider = params.get('provider') || '';
  const modelId = params.get('modelId') || '';
  const [draft, setDraft] = useState(range);
  const [providerDraft, setProviderDraft] = useState(provider);
  const [modelDraft, setModelDraft] = useState(modelId);
  const [dateError, setDateError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    setDraft({ startDate: range.startDate, endDate: range.endDate });
    setDateError(null);
  }, [range.startDate, range.endDate]);
  useEffect(() => { setProviderDraft(provider); setModelDraft(modelId); }, [provider, modelId]);

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setParams(next);
  };
  const applyRange = (next: DateRange) => { setDateError(null); update({ ...next }); };
  const applyAdvanced = () => update({ provider: providerDraft.trim() || null, modelId: modelDraft.trim() || null });
  const query = new URLSearchParams({ ...range });
  if (mode) query.set('mode', mode);
  if (provider) query.set('provider', provider);
  if (modelId) query.set('modelId', modelId);
  const report = useAnalyticsResource<GenerationQualityData>(`/api/admin/generation-quality?${query}`, refresh);
  const data = report.data;

  return <div className="page analytics-workspace generation-quality-page">
    <AnalyticsHeader title="Generation performance" action={<Link to={`/generation-usage?${new URLSearchParams({ ...range })}`}>Generation diagnostics <FiExternalLink aria-hidden="true" /></Link>} />
    <AnalyticsFilterBar>
      <AnalyticsPeriodGroup presets={<AnalyticsPeriodPresets active={(days) => periodPreset(range) === days} onSelect={(days) => applyRange(recentDays(days))} />}>
        <AnalyticsDateControl appliedStart={range.startDate} appliedEnd={range.endDate} startDate={draft.startDate} endDate={draft.endDate} startMax={hktToday()} endMax={hktToday()} error={dateError} onStartDateChange={(startDate) => setDraft((current) => ({ ...current, startDate }))} onEndDateChange={(endDate) => setDraft((current) => ({ ...current, endDate }))} onApply={(event) => {
          event.preventDefault();
          const result = validateRange(draft);
          if (!result.valid) setDateError(result.error || 'Choose a valid date range.');
          else applyRange(draft);
        }} />
      </AnalyticsPeriodGroup>
      <AnalyticsFilterGroup>
        <AnalyticsSelectField label="Mode" value={mode} options={MODE_OPTIONS} onChange={(value) => update({ mode: value || null })} />
      </AnalyticsFilterGroup>
      <AnalyticsFilterActions>
        <AnalyticsAdvancedFilters>
          <AnalyticsTextField label="Provider" value={providerDraft} onChange={setProviderDraft} onKeyDown={(event) => { if (event.key === 'Enter') applyAdvanced(); }} placeholder="Provider name" />
          <AnalyticsTextField label="Model ID" value={modelDraft} onChange={setModelDraft} onKeyDown={(event) => { if (event.key === 'Enter') applyAdvanced(); }} placeholder="Exact model ID" />
          <AnalyticsPrimaryButton onClick={applyAdvanced}>Apply</AnalyticsPrimaryButton>
        </AnalyticsAdvancedFilters>
        <AnalyticsRefreshButton onClick={() => setRefresh((current) => current + 1)} />
      </AnalyticsFilterActions>
    </AnalyticsFilterBar>
    <p className="generation-quality-definition">Server completion, success and speed. Result visibility is reported separately in Activation.</p>
    <div className="analytics-scope"><span>{range.startDate} – {range.endDate} · HKT · persisted non-preview generations · external accounts</span>{data ? <span>Updated {formatMoment(data.generatedAt)}</span> : null}</div>
    {report.error ? <section className="analytics-status" role="alert"><strong>Generation performance unavailable</strong><p>{report.error}</p><button type="button" onClick={() => setRefresh((current) => current + 1)}>Retry</button></section> : null}
    {report.loading ? <div className="analytics-status" role="status"><strong>Loading generation performance…</strong></div> : null}
    {data ? <>
      <div className="analytics-metrics generation-quality-metrics" aria-label="Generation outcomes">
        <Metric label="Server success rate" help="Completed / terminal (completed plus failed) persisted generation records in the selected period. Pending and unknown records are excluded from the denominator and shown separately." value={data.summary.successRate === null ? 'Unavailable' : formatRatio(data.summary.successRate)} note={`${count(data.summary.completed)} completed / ${count(data.summary.terminal)} terminal`} />
        <Metric label="Failed" help="Persisted generations with terminal failed status. Failed provider attempts that later recovered are not failed generations." value={count(data.summary.failed)} note={`${count(data.summary.pending)} pending · ${count(data.summary.unknown)} unknown`} />
        <Metric label="P95 completion time" help="95th percentile of creation-to-completion time for completed generations with valid timestamps. The sample and missing count are shown below." value={duration(data.latency.p95Ms)} note={`${count(data.latency.sampleCount)} timed / ${count(data.latency.eligibleCount)} eligible`} />
        <Metric label="Median completion time" help="Median creation-to-completion time among completed generations with valid timestamps. Historical rows without completedAt cannot contribute." value={duration(data.latency.p50Ms)} note={`${count(data.latency.missingCount)} completed rows missing valid timing`} />
      </div>
      <section className="analytics-panel generation-quality-outcomes" aria-labelledby="quality-outcomes-heading">
        <div className="analytics-panel-heading"><h2 id="quality-outcomes-heading">Outcome coverage</h2><span className={`generation-quality-status generation-quality-status-${data.coverage.status}`}>Report coverage: {data.coverage.status}</span></div>
        <div className="generation-quality-coverage-bar" aria-hidden="true">
          {(['completed', 'failed', 'pending', 'unknown'] as const).map((outcome) => <span key={outcome} className={`generation-quality-coverage-${outcome}`} style={{ width: `${data.summary.total > 0 ? data.summary[outcome] / data.summary.total * 100 : 0}%` }} />)}
        </div>
        <div className="generation-quality-counts"><span>Total persisted <strong>{count(data.summary.total)}</strong></span><span>Completed <strong>{count(data.summary.completed)}</strong></span><span>Failed <strong>{count(data.summary.failed)}</strong></span><span>Pending <strong>{count(data.summary.pending)}</strong></span><span>Unknown <strong>{count(data.summary.unknown)}</strong></span></div>
        <p>Success rate uses terminal records only. Provider or model filters require linked provider evidence and may reduce coverage.</p>
      </section>
      {data.byMode?.length ? <section className="analytics-panel" aria-labelledby="quality-mode-heading">
        <div className="analytics-panel-heading"><h2 id="quality-mode-heading">By creation mode</h2></div>
        <p className="generation-quality-table-note">Most persisted generations first · up to 50 recorded modes</p>
        <OutcomeTable rows={data.byMode} dimension="mode" onMode={mode => update({ mode })} />
      </section> : null}
      {data.summary.total === 0 ? <p className="analytics-notice">No persisted generations were recorded for this scope. Success rate and completion times have no samples.</p> : null}
      {data.daily?.length ? <details className="analytics-panel generation-quality-daily" open={data.summary.total > 0}>
        <summary>Daily outcomes</summary>
        <OutcomeTable rows={data.daily} dimension="date" />
      </details> : null}
      <details className="analytics-method"><summary>Definitions and coverage</summary><p>{data.latency.definition}</p>{data.coverage.notes.map((note) => <p key={note}>{note}</p>)}</details>
    </> : null}
  </div>;
}
